<template>
  <div ref="elemRef" v-bind="$attrs" class="collapsible-article relative" :style="contentStyle">
    <slot></slot>
    <div v-show="isMaskShown" class="mask absolute w-full h-30 bottom-0 left-0 pointer-events-none"></div>
  </div>
  <div v-if="showExpandBtn && isClamped && !isExpanded" class="read-more flex justify-center">
    <nd-button type="text" size="small" class="active" @click="isExpanded = true">{{ props.expandBtnText }}</nd-button>
  </div>
  <div v-if="showCollapseBtn && isClamped && isExpanded" class="read-more flex justify-center">
    <nd-button type="text" size="small" class="active" @click="isExpanded = false">{{ props.collapseBtnText }}</nd-button>
  </div>
</template>

<script setup lang="ts">
import type { StyleValue } from 'vue'

const props = withDefaults(
  defineProps<{
    showExpandBtn?: boolean
    expandBtnText?: string
    showCollapseBtn?: boolean
    collapseBtnText?: string
    lineNum?: number
    maxHeight?: number
    mask?: boolean
  }>(),
  {
    showExpandBtn: true,
    expandBtnText: '顯示更多',
    showCollapseBtn: false,
    collapseBtnText: '顯示部分內容',
    lineNum: 3,
    maxHeight: 0,
    mask: false,
  }
)

const isClamped = ref(false)
const isExpanded = ref(false)
const elemRef = ref<Nullable<HTMLElement>>(null)

const mode = computed(() => (props.maxHeight ? 'max-height' : 'line-clamp'))
const isMaskShown = computed(() => props.mask && isClamped.value && !isExpanded.value)

const contentStyle = computed<StyleValue>(() => {
  if (mode.value === 'max-height') {
    return {
      maxHeight: isExpanded.value ? 'none' : `${props.maxHeight}px`,
      overflow: 'hidden',
    }
  } else if (mode.value === 'line-clamp') {
    return {
      display: isExpanded.value ? 'block' : '-webkit-box',
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': isExpanded.value ? 'none' : `${props.lineNum}`,
    }
  }
  return {}
})

const checkClamp = () => {
  if (elemRef.value) {
    if (mode.value === 'max-height') {
      isClamped.value = !isExpanded.value && elemRef.value.scrollHeight > props.maxHeight
    } else if (mode.value === 'line-clamp') {
      isClamped.value = elemRef.value.scrollHeight > elemRef.value.clientHeight
    }
  }
}

useEventListener('resize', checkClamp)

onMounted(() => {
  checkClamp()
  isExpanded.value = !isClamped.value
})
</script>

<style lang="scss" scoped>
.collapsible-article {
  .mask {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
  }
}
</style>
